import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Stack,
  Grid,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';

interface AuditLog {
  id: string;
  created_at: string;
  user_email?: string;
  user_name?: string;
  action: string;
  query_string?: string;
  api_key_id?: string;
  source_ip?: string;
  status: string;
  execution_time_ms?: number;
  result_count?: number;
  error_message?: string;
  api_route?: string;
}

interface Statistics {
  totalRecords: number;
  recordsBy30Days: number;
  recordsBy7Days: number;
  recordsByAction?: Record<string, number>;
  recordsByStatus?: Record<string, number>;
}

export default function AuditLogsPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);

  // Filters
  const [userEmail, setUserEmail] = useState('');
  const [action, setAction] = useState('');
  const [status, setStatus] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteEmail, setDeleteEmail] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchStatistics();
    fetchAuditLogs();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [userEmail, action, status, startDate, endDate]);

  const getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken') || localStorage.getItem('jwtToken');
    if (token) {
      return { 'Authorization': `Bearer ${token}` };
    }
    return {};
  };

  const fetchStatistics = async () => {
    try {
      const response = await fetch('/api/audit/statistics', {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setStatistics(data);
      }
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  };

  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(rowsPerPage),
        offset: String(page * rowsPerPage),
        ...(userEmail && { userEmail }),
        ...(action && { action }),
        ...(status && { status }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
      });

      const response = await fetch(`/api/audit/logs?${params}`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(data.records);
        setTotalRecords(data.total);
      }
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchAuditLogs();
  };

  const handleReset = () => {
    setUserEmail('');
    setAction('');
    setStatus('');
    setStartDate('');
    setEndDate('');
    setPage(0);
  };

  const handleExport = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      const params = new URLSearchParams({
        startDate: startDate || weekAgo + 'T00:00:00Z',
        endDate: endDate || today + 'T23:59:59Z',
      });

      const response = await fetch(`/api/audit/export?${params}`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const { data, fileName } = await response.json();
        const blob = new Blob([data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to export:', error);
    }
  };

  const handleDeleteOpen = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setDeleteEmail('');
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      const response = await fetch('/api/audit/delete-user-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          userEmail: deleteEmail,
          scope: 'all',
          reason: 'Manual deletion via admin panel',
        }),
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Deleted ${result.deletedCount} audit records for ${deleteEmail}`);
        setDeleteEmail('');
        setDeleteDialogOpen(false);
        fetchAuditLogs();
        fetchStatistics();
      } else {
        alert('Failed to delete audit records');
      }
    } catch (error) {
      console.error('Failed to delete:', error);
      alert('Error during deletion');
    } finally {
      setDeleteLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'unauthorized':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1400 }}>
      <Typography variant="h4" sx={{ mb: 1 }}>
        📊 Audit Log Management
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        Search, filter, and manage audit logs. Logs are retained for 90 days and automatically exported to file share.
      </Typography>

      {/* Statistics */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2, textAlign: 'center' }}>
              <Typography color="textSecondary" gutterBottom>
                Total Records
              </Typography>
              <Typography variant="h5">{statistics.totalRecords.toLocaleString()}</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2, textAlign: 'center' }}>
              <Typography color="textSecondary" gutterBottom>
                Last 30 Days
              </Typography>
              <Typography variant="h5" sx={{ color: 'primary.main' }}>
                {statistics.recordsBy30Days.toLocaleString()}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2, textAlign: 'center' }}>
              <Typography color="textSecondary" gutterBottom>
                Last 7 Days
              </Typography>
              <Typography variant="h5" sx={{ color: 'success.main' }}>
                {statistics.recordsBy7Days.toLocaleString()}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2, textAlign: 'center' }}>
              <Typography color="textSecondary" gutterBottom>
                Success Rate
              </Typography>
              <Typography variant="h5" sx={{ color: 'success.main' }}>
                {statistics.recordsByStatus?.success && statistics.totalRecords
                  ? (
                      (statistics.recordsByStatus.success / statistics.totalRecords) *
                      100
                    ).toFixed(1)
                  : '0'}
                %
              </Typography>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          🔍 Filter Logs
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="User Email"
              size="small"
              fullWidth
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              placeholder="john@example.com"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Action"
              size="small"
              fullWidth
              value={action}
              onChange={(e) => setAction(e.target.value)}
              placeholder="semantic_search"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Status"
              size="small"
              fullWidth
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              placeholder="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="From Date"
              type="date"
              size="small"
              fullWidth
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="To Date"
              type="date"
              size="small"
              fullWidth
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
            disabled={loading}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleReset}
            disabled={loading}
          >
            Reset
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={loading}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteOpen}
          >
            Delete User Data
          </Button>
        </Stack>
      </Card>

      {/* Audit Logs Table */}
      <Card>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : logs.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="textSecondary">No audit logs found</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>API Route</TableCell>
                    <TableCell>Query</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Time (ms)</TableCell>
                    <TableCell align="right">Results</TableCell>
                    <TableCell>IP</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell sx={{ fontSize: '0.85rem' }}>
                        {new Date(log.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>
                        {log.user_name} ({log.user_email})
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>{log.action}</TableCell>
                      <TableCell
                        sx={{ fontSize: '0.85rem', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={log.api_route}
                      >
                        {log.api_route || 'N/A'}
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: '0.85rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={log.query_string}
                      >
                        {log.query_string?.substring(0, 40)}...
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.status}
                          size="small"
                          color={getStatusColor(log.status)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.85rem' }}>
                        {log.execution_time_ms}ms
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.85rem' }}>
                        {log.result_count || 0}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>{log.source_ip}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={totalRecords}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </Card>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteClose} maxWidth="sm" fullWidth>
        <DialogTitle>Delete User Audit Data</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2, color: 'error.main' }}>
              ⚠️ This action is permanent! All audit records for the user will be deleted.
            </Typography>
            <TextField
              label="User Email"
              type="email"
              fullWidth
              value={deleteEmail}
              onChange={(e) => setDeleteEmail(e.target.value)}
              placeholder="user@example.com"
              disabled={deleteLoading}
            />
            <Typography variant="caption" sx={{ mt: 2, display: 'block', color: 'text.secondary' }}>
              Enter the exact email address of the user whose audit records should be deleted.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={!deleteEmail || deleteLoading}
          >
            {deleteLoading ? 'Deleting...' : 'Delete All Records'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
